import {yupResolver} from '@hookform/resolvers/yup'
import {
    Box, Card, CardContent,
    Grid,
    Typography
} from '@mui/material'
import {useEffect, useState} from 'react'
import {useForm} from 'react-hook-form'
import {toast} from 'react-toastify'
import {useRecoilValue} from 'recoil'
import {IntermediateGlobalLoading} from '../../../../Component/Common/Components/Loading/IntermediateGlobalLoading'
import {ImportSource} from '../../../../Component/Model/Enum/ImportEnum'
import {defaultObject} from '../../../../Helpers/typescriptHelper'
import config from '../../../../config'
import {
    VerificationWorkflowService
} from '../../../../Component/Api/VerificationAgency/VerificationWorkflow/VerificationWorkflowService'
import {OrganisationAtom} from "../../../../State/Organisation.Atom";
import {
    ImportManagement
} from "../../../../Component/Model/Dto/VerificationAgency/Client/ImportManagement/ImportManagement";
import {PayrollImportValidationSchema} from "./PayrollImportValidationSchema";
import AppAccordion from "../../../../Component/Common/Components/Accordion/AppAccordion";
import {MDButton} from "../../../../Component/Common/Components/MDButton";
import { ImportProgressBar } from '../../../../Component/Common/Components/ProgressBar/ImportProgressBar'
import {useSignalRConnection} from "../../../../Hooks/useSignalRConnection";

export const onDownload = (href: string) => {
    const link = document.createElement("a");
    link.download = `errorLog.csv`;
    link.href = href;
    link.click();
};

export const PayrollImportDetails = () => {
    const organisation = useRecoilValue(OrganisationAtom);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [progressData, setProgressData] = useState<{
        [scorecardId: string]: { [importTypeId: number]: ImportManagement.ImportDetailSheetDto }
    }>({});
    const [importDetails, setImportDetails] = useState<ImportManagement.ImportDetailDto[]>([]);
    const [useSignalR, setUseSignalR] = useState<boolean>(false);
    const [organisationID, setOrganisationID] = useState<number>();
    const hub = useSignalRConnection("ImportHub");

    const {reset} = useForm({
        mode: 'all',
        resolver: yupResolver(PayrollImportValidationSchema)
    });

       useEffect(() => {
           if (organisation.OrganisationId > 0) {
               const updatedOrganisationData = {
                   ...defaultObject,
                   organisationId: organisation.OrganisationId
               };
               reset(updatedOrganisationData);
           }
       }, []);
   
       useEffect(() => {
           if (organisation.OrganisationId !== undefined && organisation.OrganisationId > 0) {
                setOrganisationID(organisation.OrganisationId);
               getOrganisationImportsInProgress(organisation.OrganisationId);
           } 
       }, [organisation.OrganisationId]);

    const getOrganisationImportsInProgress = async (organisationId: number) => {
        try {
            const organisationImportDetails = await VerificationWorkflowService.GetOrganisationImportDetails(organisationId, ImportSource.Payroll);

            if (organisationImportDetails.data?.length > 0) {
                const initialProgressData: {
                    [scorecardId: string]: { [importTypeId: number]: ImportManagement.ImportDetailSheetDto }
                } = {};

                organisationImportDetails.data.forEach(importDetail => {
                    const scorecardId = importDetail.scorecardId;
                    initialProgressData[scorecardId] = {};

                    importDetail.progressSheets?.forEach(section => {
                        initialProgressData[scorecardId][section.importTypeId] = {...section};
                    });
                });

                setProgressData(initialProgressData);
                setImportDetails(organisationImportDetails.data);
                setUseSignalR(true);
            } 
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }

    const updateProgress = (
        scorecardId: string,
        importTypeId: number,
        updates: Partial<ImportManagement.ImportDetailSheetDto>
    ) => {
        setProgressData(prev => ({
            ...prev,
            [scorecardId]: {
                ...(prev[scorecardId] ?? {}),
                [importTypeId]: {
                    ...(prev[scorecardId]?.[importTypeId] ?? {}),
                    ...updates,
                },
            },
        }));
    };

    useEffect(() => {
        if (!useSignalR) return;

        const handlers: { eventName: string; callback: (...args: any[]) => void }[] = [];

        const registerHandler = (eventName: string, callback: (...args: any[]) => void) => {
            hub.on(eventName, callback);
            handlers.push({ eventName, callback });
        };

        (async () => {
            try {
                if (!hub.isConnected) {
                    await hub.start();
                }

                importDetails.forEach(importDetail => {
                    const scorecardId = importDetail.scorecardId;
                    const importManagementId = importDetail.id;
                    const identifier = `payroll-${scorecardId}-${organisationID}-${importManagementId}`;
                    registerHandler(
                        `ImportInProgress-${identifier}`,
                        (data: any) => {
                            const {
                                CurrentScorecard,
                                ImportTypeId,
                                progress,
                                LastSuccessfulStep
                            } = data;

                            updateProgress(CurrentScorecard, ImportTypeId, {
                                progressPercentage: progress,
                                lastSuccessfulStep: LastSuccessfulStep,
                            });
                        }
                    );

                    registerHandler(
                        `ImportSuccess-${identifier}`,
                        (data: any) => {
                            const {
                                CurrentScorecard,
                                ImportTypeId,
                                NumberOfRowsCorrect,
                                NumberOfRowsFailed,
                                Log
                            } = data;

                            updateProgress(CurrentScorecard, ImportTypeId, {
                                progressPercentage: 100,
                                importSuccessful: true,
                                numberOfRowsCorrect: NumberOfRowsCorrect,
                                numberOfRowsFailed: NumberOfRowsFailed,
                                log: Log,
                            });
                        }
                    );

                    registerHandler(
                        `ImportFailed-${identifier}`,
                        (data: any) => {
                            const {
                                CurrentScorecard,
                                ImportTypeId,
                                NumberOfRowsCorrect,
                                NumberOfRowsFailed,
                                Log,
                                ErrorBlobUrl,
                                LastSuccessfulStep,
                                isCompleted
                            } = data;

                            if (CurrentScorecard !== undefined && ImportTypeId !== undefined) {
                                updateProgress(CurrentScorecard, ImportTypeId, {
                                    progressPercentage: 0,
                                    importSuccessful: false,
                                    numberOfRowsCorrect: NumberOfRowsCorrect,
                                    numberOfRowsFailed: NumberOfRowsFailed,
                                    log: Log,
                                    errorBlobUrl: ErrorBlobUrl,
                                    lastSuccessfulStep: LastSuccessfulStep,
                                });
                            }

                            if (isCompleted !== undefined && !isCompleted) {
                                toast.error(`Import Failed for ${importDetail.scorecardName}. Please try again`)
                            }
                        }
                    );
                });

                registerHandler(
                    `ImportFailed-payroll-${organisationID}`,
                    () => {
                        toast.error(`Import Failed for selected scorecards. Please try again`);
                    });

                registerHandler(
                    `ImportComplete-payroll-${organisationID}`,
                    () => {
                    });

            } catch (error) {
                console.error("SignalR connection failed:", error);
            }
        })();

        return () => {
            handlers.forEach(h => hub.off(h.eventName, h.callback));
        };
    }, [useSignalR, importDetails]);


    if (isLoading) {
        return <IntermediateGlobalLoading/>;
    };


    return (
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Typography variant="h4" fontWeight="bold" mb={3}>
                        Central Data Management - Payroll Imports
                    </Typography>
                </Grid>

                {importDetails.length > 0 &&
                    Object.entries(progressData).map(([scorecardId, accordionSection]) => {
                        const importDetail = importDetails.find(
                            (d) => d.scorecardId === scorecardId
                        );
                        const labelName = `${importDetail?.organisationName} | ${
                            importDetail?.isEEScorecard ? 'EE' : 'BEE'
                        } | ${importDetail?.scorecardName}`;

                        return (
                            <Grid item xs={12} key={scorecardId}>
                                <AppAccordion expandOnDefault={true} label={labelName}>
                                    <Box width="100%">
                                        {Object.entries(accordionSection).map(
                                            ([importTypeId, section]) => (
                                                <Card
                                                    key={importTypeId}
                                                    variant="outlined"
                                                    sx={{ mb: 2 }}
                                                >
                                                    <CardContent>
                                                        <Typography
                                                            variant="subtitle1"
                                                            fontWeight="small"
                                                            mb={2}
                                                        >
                                                            {section.dataPageName}
                                                        </Typography>

                                                        <ImportProgressBar
                                                            value={
                                                                section.progressPercentage ?? 0
                                                            }
                                                            progressData={{
                                                                progress: section.progressPercentage,
                                                                numberOfRowsCorrect:
                                                                    section.numberOfRowsCorrect,
                                                                numberOfRowsFailed:
                                                                    section.numberOfRowsFailed,
                                                                log:
                                                                    section.importSuccessful &&
                                                                    section.numberOfRowsFailed === 0
                                                                        ? []
                                                                        : [''],
                                                                isSuccess:
                                                                    section.importSuccessful,
                                                                errorLogUrl:
                                                                    section.errorBlobUrl,
                                                                lastSuccessfulStep:
                                                                    section.lastSuccessfulStep,
                                                            }}
                                                            errorLogUrl={section.errorBlobUrl}
                                                            isExcelImport={true}
                                                        />

                                                        {section.numberOfRowsFailed > 0 && (
                                                            <Box mt={2}>
                                                                <Typography
                                                                    color="error"
                                                                    mb={1}
                                                                >
                                                                    Import Failed – Number
                                                                    of failed rows:{' '}
                                                                    {
                                                                        section.numberOfRowsFailed
                                                                    }
                                                                </Typography>
                                                                <MDButton
                                                                    color="error"
                                                                    variant="outlined"
                                                                    onClick={() =>
                                                                        onDownload(
                                                                            section.errorBlobUrl!
                                                                        )
                                                                    }
                                                                >
                                                                    Download Error Log
                                                                </MDButton>
                                                                <Typography
                                                                    variant="caption"
                                                                    display="block"
                                                                    mt={1}
                                                                >
                                                                    We encountered an issue
                                                                    with several rows. To see
                                                                    the complete list, kindly
                                                                    download the error log.
                                                                </Typography>
                                                            </Box>
                                                        )}

                                                        {section.importSuccessful &&
                                                            section.numberOfRowsFailed === 0 && (
                                                                <Typography
                                                                    color="success.main"
                                                                    mt={2}
                                                                >
                                                                    Number of correct rows:{' '}
                                                                    {
                                                                        section.numberOfRowsCorrect
                                                                    }
                                                                </Typography>
                                                            )}
                                                    </CardContent>
                                                </Card>
                                            )
                                        )}
                                    </Box>
                                </AppAccordion>
                            </Grid>
                        );
                    })}
            </Grid>
        );
};